// Slice 6: tiny read-only hook that surfaces per-plant counts of
// app-using subscriber locations into the Plants list. Lives separate
// from useManufacturingPlantDealersTab (which is already over the
// 300-line soft cap) so the dealersTab orchestrator stays untouched.
//
// Reads:
//   - GET /api/mfg/subscriber-locations  (the slice-4 endpoint that
//     returns public.locations + mfg_subscriber_location_settings overlay)
//   - GET /api/mfg/dealers                (already loaded elsewhere, but
//     refetched here for self-containment + to derive the per-subscriber-org
//     default plant id needed for inheritance resolution)
//
// Computes effective-plant tallies via
// tallySubscriberLocationsByEffectivePlant so the Plants list chip
// agrees byte-for-byte with what the slice-5 Dealer-locations slider
// shows in its App-using tab.
//
// Note (dedup follow-up): the slice-5 tabbed wrapper ALSO calls these
// two endpoints from its own hooks. A future slice can lift the
// shared subscriber-locations + dealers reads to the panel level and
// pass them down to both consumers. Out of scope here — slice 6 is a
// visibility-only change.
import { useEffect, useMemo, useState } from "react";
import {
  listManufacturingDealers,
  listManufacturingSubscriberLocations,
} from "@/lib/manufacturing/dealersClient";
import type { MfgPlant } from "@/lib/manufacturingClient";
import type {
  MfgSubscriberDealer,
  MfgSubscriberLocation,
} from "@/lib/manufacturing/dealerTypes";
import {
  buildDealerOrgPlantId,
  buildPlantById,
  defaultPlantOf,
} from "./dealerLocationPlantResolver";
import {
  tallySubscriberLocationsByEffectivePlant,
  type SubscriberLocationPlantCounts,
} from "./plantDealersFilter";

const EMPTY_COUNTS: SubscriberLocationPlantCounts = {
  byPlantId: new Map(),
  unassigned: 0,
};

export function useManufacturingPlantSubscriberLocationCounts(
  plants: ReadonlyArray<MfgPlant>,
  refreshTick: number
): SubscriberLocationPlantCounts {
  const [subscriberDealers, setSubscriberDealers] = useState<MfgSubscriberDealer[]>([]);
  const [subscriberLocations, setSubscriberLocations] = useState<MfgSubscriberLocation[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [d, l] = await Promise.all([
        listManufacturingDealers(),
        listManufacturingSubscriberLocations(),
      ]);
      if (cancelled) return;
      if (d.ok) setSubscriberDealers(d.data.subscriber_dealers);
      if (l.ok) setSubscriberLocations(l.data.subscriber_locations);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  return useMemo(() => {
    if (subscriberLocations.length === 0) return EMPTY_COUNTS;
    return tallySubscriberLocationsByEffectivePlant(
      subscriberLocations,
      buildDealerOrgPlantId(subscriberDealers),
      defaultPlantOf(plants),
      buildPlantById(plants)
    );
  }, [subscriberLocations, subscriberDealers, plants]);
}
